
import { useState, useMemo, useEffect, useCallback } from 'react';
// Fix: Use named import for useReactFlow from reactflow.
import { useReactFlow } from 'reactflow';
import { AppNode } from '../types';

interface UseSearchProps {
  nodes: AppNode[];
  onSelectNode: (nodeId: string) => void;
}

export const useSearch = ({ nodes, onSelectNode }: UseSearchProps) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const { setCenter } = useReactFlow();

  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    return nodes.filter(node => 
      node.data.label?.toLowerCase().includes(lowerQuery) || 
      node.data.description?.toLowerCase().includes(lowerQuery)
    ).map(node => node.id);
  }, [nodes, query]);

  // Reset active index when matches change
  useEffect(() => {
    if (matches.length > 0) {
      setActiveIndex(0);
    } else {
      setActiveIndex(-1);
    }
  }, [matches.length]);

  const navigateToMatch = useCallback((index: number, isInitial: boolean = false) => {
    if (index < 0 || index >= matches.length) return;
    
    const nodeId = matches[index];
    const node = nodes.find(n => n.id === nodeId);
    
    if (node) {
      onSelectNode(nodeId);
      
      // Calculate center of node (standard node width 180, height 64)
      const centerX = node.position.x + 90;
      const centerY = node.position.y + 32;
      
      setCenter(centerX, centerY, { 
        zoom: 1.2, 
        duration: isInitial ? 800 : 600 
      });
    }
  }, [matches, nodes, onSelectNode, setCenter]);

  // Navigate whenever active index changes and is valid
  useEffect(() => {
    if (activeIndex >= 0) {
      navigateToMatch(activeIndex, activeIndex === 0);
    }
  }, [activeIndex, navigateToMatch]);

  const nextMatch = () => {
    if (matches.length === 0) return;
    setActiveIndex(prev => (prev + 1) % matches.length);
  };

  const handleSearchChange = (val: string) => {
    setQuery(val);
  };

  return {
    query,
    setQuery: handleSearchChange,
    matches,
    activeIndex,
    nextMatch,
  };
};
